/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/routing/route-disposition.ts
 * layer: web
 * area: config
 * kind: source
 * web_root: config
 * relpath: web/src/app/config/route-disposition.ts
 * filename: route-disposition.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

// Machine-readable ownership of active routes and destination capabilities.
//
// Per the Block A dispatch prompt (F4 mitigation), we enumerate ALL routes
// — not just the 79 sidebar-facing ones — and classify each as one of:
//
//   - `keep`      — a justified public, platform, destination, or design route.
//   - `capability` — existing functionality hosted contextually by one of the
//                    five destination routers.
//   - `retired`   — a verified dead path with no implementation or emitter.
//
// Every entry carries `destination` so the route crawler can assert that the
// active destination hint is consistent with the plan.
//
// IMPORTANT: this file is the authoritative source for `App.tsx`. If you add
// a route, add its disposition here first; if you remove one, remove it from
// App.tsx and this table simultaneously.

import type { DestinationId } from '@/app/config/destinations';
import { WORKBENCH_PATHS } from '@/app/navigation/routes/workbenchPaths';

export type Disposition = 'keep' | 'capability' | 'retired';

export interface RouteDisposition {
  /** Capability identity or active path pattern (may include `:param`). */
  originalPath: string;
  /** Which destination this route belongs to under the new shell. */
  destination: DestinationId;
  /** What happens to the route. */
  disposition: Disposition;
  /**
   * Reserved for schema compatibility. Active entries never redirect.
   */
  target: string | null;
  /** Human-readable note — mostly for crawler-report readability. */
  note?: string;
}

/**
 * Complete disposition table.
 *
 * Ordered roughly by domain for readability (auth/public first, then each
 * major module). The crawler iterates the table in order — earlier entries
 * match first.
 */
export const ROUTE_DISPOSITIONS: readonly RouteDisposition[] = [
  // -------------------------------------------------------------------------
  // Public / auth — not in the AppShell, render standalone.
  // -------------------------------------------------------------------------
  {
    originalPath: '/login',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'public',
  },
  {
    originalPath: '/auth/callback',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'public',
  },
  {
    originalPath: '/login/redeem',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'public customer portal magic-link redemption',
  },
  {
    originalPath: '/portal/*',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'standalone customer support portal, no employee shell',
  },
  {
    originalPath: '/recovery',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'public',
  },
  {
    originalPath: '/suspended',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'public',
  },
  {
    originalPath: '/mfa/pin-setup',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'mfa',
  },
  {
    originalPath: '/proposals/:proposalId/view',
    destination: 'runway',
    disposition: 'keep',
    target: null,
    note: 'standalone proposal preview, no shell',
  },
  {
    originalPath: '/remote-session/:id',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'popup remote session, no shell',
  },

  // -------------------------------------------------------------------------
  // Platform shell — platform-tenant-only; not part of the 5-destination UX.
  // -------------------------------------------------------------------------
  { originalPath: '/platform', destination: 'switchboard', disposition: 'keep', target: null },
  {
    originalPath: '/platform/tenants',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  {
    originalPath: '/platform/tenants/:id',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  {
    originalPath: '/platform/licensing',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  {
    originalPath: '/platform/api-keys',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  {
    originalPath: '/platform/billing',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  {
    originalPath: '/platform/health',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  { originalPath: '/platform/m365', destination: 'switchboard', disposition: 'keep', target: null },
  {
    originalPath: '/platform/feature-flags',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },
  {
    originalPath: '/platform/audit',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Role-aware application landing.
  // -------------------------------------------------------------------------
  {
    originalPath: '/',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'role landing',
  },

  // Ambient role views — these are real pages that engineers/dispatchers
  // live in daily. Keep the routes but they belong to Workbench.

  // -------------------------------------------------------------------------
  // Workbench destination — the owned basePath.
  // -------------------------------------------------------------------------
  {
    originalPath: '/workbench',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'destination root',
  },
  {
    originalPath: '/library/atoms',
    destination: 'library',
    disposition: 'keep',
    target: null,
    note: 'canonical executable capability registry',
  },

  // -------------------------------------------------------------------------
  // Service Operations PSA — tickets, work items, billing, reports, dispatch, calls.
  // Tickets/work-items live in Workbench. Reports belong to Portfolio.
  // Billing belongs to Portfolio (financial view).
  // -------------------------------------------------------------------------
  {
    originalPath: '/tickets',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'ticket list, Workbench queue drill-in',
  },
  {
    originalPath: '/tickets/:id',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tickets/:id/review',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tickets/kanban',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tickets/merge',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/known-problems',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'durable root-cause and workaround records for recurring incidents',
  },
  { originalPath: '/workitems', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/workitems/:id',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/workitems/kanban',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/kanban',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/:projectId/kanban',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/templates',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/pipelines',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/time/log', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/billing',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
    note: 'financial dashboard',
  },
  { originalPath: '/reports', destination: 'portfolio', disposition: 'capability', target: null },
  {
    originalPath: '/reports/time-savings',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/calls', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/calls/:sessionId',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/calls/voicemails',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-intelligence',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/identity-lifecycle',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'user-lifecycle runway surfaces',
  },
  {
    originalPath: '/identity-lifecycle/onboarding',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/identity-lifecycle/offboarding',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/audit/:id',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/approvals', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/operations',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/resolve', destination: 'workbench', disposition: 'capability', target: null },

  // Service Operations/Settings aliases (kept for continuity of existing links).

  // -------------------------------------------------------------------------
  // Device Management RMM — device, agent, remote, monitoring, backup.
  // Monitoring & device management belong to Workbench (queue feed).
  // Policy / admin pages belong to Settings.
  // -------------------------------------------------------------------------
  { originalPath: '/devices', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/devices/:id',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/devices/:id/health',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/devices/:deviceId/files',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-groups',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/remote', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/remote/:id',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/remote/:deviceId/files',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/remote/recordings',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/remote/recordings/:recordingId',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/patches', destination: 'workbench', disposition: 'capability', target: null },
  { originalPath: '/agents', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/agents/deploy',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/agents/deploy/push',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/agents/updates',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/agents/:id/logs',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },

  // /device-management/* cluster
  {
    originalPath: '/devices/install-links',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/devices/fleet-overview',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/devices/broadcast',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/network/topology',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/network/configs',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/network-monitoring/alerts',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/ups',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/device-assessment',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/site-assessment',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/mft',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/mft/jobs',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/mft/jobs/new',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/mft/jobs/:jobId',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/mft/jobs/:jobId/edit',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/mft/endpoints',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/app-store',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/app-store/deployments',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/app-store/removal',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/config-policies/:id',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/config/baselines',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/config/drift',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/config/gpo',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/devices/:id/event-log',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/network/docs',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/network/credentials',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/network-monitoring',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/devices/:id/registry',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/hygiene',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/hygiene/:policyId',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/scripts/:scriptId',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/endpoint-runtime',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/auto-provision',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/device-management/fleet-health',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },

  // Playbooks & mjolnir & vendor & script
  { originalPath: '/playbooks', destination: 'library', disposition: 'capability', target: null },
  {
    originalPath: '/playbooks/:id',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/mjolnir', destination: 'library', disposition: 'capability', target: null },
  {
    originalPath: '/mjolnir/sessions',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/mjolnir/playbooks',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/profiles',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/deploy',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/status',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/huntress',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/dnsfilter',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/dashlane',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/ironscales',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/cloudally',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/codetwo',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/printix',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/dashboard',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/deployments',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/onboarding',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/cloudflare',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/vendor/security-suite',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },

  // EmailSignatures — email signatures
  {
    originalPath: '/email-signatures',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/signatures',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/signatures/gallery',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/signatures/new',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/signatures/:templateId',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/campaigns',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/deployments',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/health',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'four-layer Iris deployment and client acceptance evidence',
  },
  {
    originalPath: '/email-signatures/customers',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'per-customer Iris ownership, rules, preview and rollout',
  },
  {
    originalPath: '/email-signatures/templates',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/bginfo',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/ooo',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/email-signatures/fonts',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/signatures/editor',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/signatures/editor/:signatureId',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },

  // Tenant governance
  {
    originalPath: '/tenant-governance',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tenant-governance/licenses',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tenant-governance/usage',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tenant-governance/mfa',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tenant-governance/admin-guard',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },

  // Prometheus — agent releases
  {
    originalPath: '/prometheus',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/prometheus/releases',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/prometheus/rollout',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/prometheus/compliance',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Customer Lifecycle — CRM, customers, proposals, contracts.
  // Customers belong to Portfolio. Proposals belong to Runway.
  // -------------------------------------------------------------------------
  {
    originalPath: '/onboarding/new-client',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/proposals', destination: 'runway', disposition: 'capability', target: null },
  { originalPath: '/contracts', destination: 'portfolio', disposition: 'capability', target: null },
  { originalPath: '/contacts', destination: 'portfolio', disposition: 'capability', target: null },
  {
    originalPath: '/contacts/:id',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/proposals/pipeline',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/proposals/library',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/proposals/:proposalId/compose',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/service-catalog',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/pricing', destination: 'library', disposition: 'capability', target: null },
  {
    originalPath: '/profitability',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/gaps',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/prices',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/engagement',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/renewals',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/qbr', destination: 'portfolio', disposition: 'capability', target: null },
  {
    originalPath: '/customer-lifecycle/day1',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/day1/:planId',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/presentations',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/satisfaction',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/risk-register',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tech-roadmaps',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tech-roadmaps/:roadmapId',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/tech-roadmap-templates',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator/list',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator/catalogue',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator/compare',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator/terms',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator/rules',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/customer-lifecycle/estimator/:id',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  { originalPath: '/roadmaps', destination: 'portfolio', disposition: 'capability', target: null },
  {
    originalPath: '/roadmaps/:id',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // Procurement — procurement
  {
    originalPath: '/procurement',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
    note: 'procurement portfolio overview',
  },
  {
    originalPath: '/procurement/products',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/procurement/orders',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/procurement/orders/:id',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/procurement/tracking',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/procurement/hardware-profiles',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/procurement/bundles',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/procurement/microsoft-licenses',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Vault — Cerberus + Credential Health
  // -------------------------------------------------------------------------
  { originalPath: '/vault', destination: 'switchboard', disposition: 'capability', target: null },
  {
    originalPath: '/vault/health/scans/:id',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Asset Lifecycle — assets
  // -------------------------------------------------------------------------
  { originalPath: '/assets', destination: 'portfolio', disposition: 'capability', target: null },
  {
    originalPath: '/assets/certificates',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/licenses',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/renewals',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/warranties',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/leases',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/disposal',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/refresh-cycle',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/registry',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/service-desk',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/site-docs',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/uptime',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assets/domains',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Backup Management — backups
  // -------------------------------------------------------------------------
  { originalPath: '/backups', destination: 'workbench', disposition: 'capability', target: null },
  {
    originalPath: '/backups/providers',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/providers/new',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/jobs',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/compliance',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/reports',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/alerts',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/scopes',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/restore-tests',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/backups/restore-tests/new',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Observe, drift, automation, monitoring.
  // -------------------------------------------------------------------------
  {
    originalPath: '/drift/baselines',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/drift/policies',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/configuration-drift/policies',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/automation/rules',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/automation/scripts',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/automation/workflows',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/automation/executions',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/automation/schedules',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/automation/webhooks',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: WORKBENCH_PATHS.monitoring,
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'canonical Workbench monitoring cockpit',
  },

  // Process authoring moved from Library into Runway. These remain as
  // bookmark-safe aliases until route telemetry proves they can be retired.

  // -------------------------------------------------------------------------
  // M365 & compliance & security
  // -------------------------------------------------------------------------
  {
    originalPath: '/m365/usage',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/inactive-users',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/sign-in-logs',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/risky-agents',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/intune-remediations',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/intune-enrollment-posture',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/intune-app-approvals',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/m365/power-automate',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/assessor/deadlines',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/security/vulnerabilities',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/security/incidents',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/compliance/*',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
    note: 'compliance sub-routes render via useRoutes',
  },

  // -------------------------------------------------------------------------
  // Reports — builder + history belong to Library, viewing to Portfolio.
  // -------------------------------------------------------------------------
  {
    originalPath: '/reports/history',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/reports/schedules',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // Call flows / workflows designer
  {
    originalPath: '/callflows/designer',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/callflows/designer/:id',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/callflows/auto-attendant/new',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/workflows/designer',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/workflows/designer/:id',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },

  // Projects — Project Delivery
  {
    originalPath: '/projects/:projectId/gantt',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/guided',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/guided/:id',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/:id/board',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/:id/builder',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/projects/guided/:id/builder',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },

  // Cloud governance, communications-migration, migration
  {
    originalPath: '/cloud-governance',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/cloud-governance/rate-limits',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/cloud-governance/recordsroom',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  // Communications Migration stays under /communications-migration — migration tooling is dev-only (Rule 3).
  // We keep it routable as a drill-in under Settings so existing internal
  // links don't 404, but it never surfaces in the new sidebar sub-nav.
  {
    originalPath: '/communications-migration',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3: dev-only migration tooling',
  },
  {
    originalPath: '/communications-migration/export',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/communications-migration/history',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/communications-migration/assessment',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/migration',
    destination: 'switchboard',
    disposition: 'retired',
    target: null,
    note: 'retired WPF-to-cloud Migration Wizard: data export, credential, agent and validation steps were simulations or "coming soon"',
  },
  {
    originalPath: '/migration/device',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/migration/device/new',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/migration/gworkspace/new',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/migration/device/:id',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },
  {
    originalPath: '/migration/profile/:id',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3',
  },

  // CW RMM import (dev tool)
  {
    originalPath: '/cwrmm-import',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'Rule 3 dev-only import',
  },

  // -------------------------------------------------------------------------
  // Tenant setup and tenant administration.
  // -------------------------------------------------------------------------
  {
    originalPath: '/tenant-setup',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tenant-administration',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/tenant-administration/tenants',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/billing/subscribe',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/billing/success',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/billing/cancel',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Admin / Settings area — merges into the Settings destination sub-menu.
  // -------------------------------------------------------------------------
  {
    originalPath: '/search',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'global search results page',
  },
  {
    originalPath: '/notifications',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/tenant-compare',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/scan-schedules',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/kb/new',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/kb/:id',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/custom-fields',
    destination: 'switchboard',
    disposition: 'retired',
    target: null,
    note: 'retired phantom route: no component, internal emitter, or field-definition API exists',
  },
  {
    originalPath: '/admin/devices/policies',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/devices/policies/:id',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/devices/agent-config',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/devices/alert-catalog',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/devices/alert-dedup',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/devices/group-rules',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/operations/ticket-templates',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/operations/canned-responses',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/operations/billing-config',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/operations/project-templates',
    destination: 'library',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/admin/insights',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Insights — all belong to Portfolio's Analytics sub-menu.
  // -------------------------------------------------------------------------
  {
    originalPath: '/insights/benchmark',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/compliance-matrix',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/resolution-feed',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/delivery-pipeline',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/engineer-workload',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/client-health',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/client/:customerId',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/cost-of-inaction',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/automation',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/insights/revenue',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Attack sim (Defender), personas, trust stream.
  // -------------------------------------------------------------------------
  {
    originalPath: '/personas',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/personas/new',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/personas/:id/edit',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/personas/queue',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/personas/logs',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/trust-stream',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
  },
  {
    originalPath: '/disruption/plans',
    destination: 'runway',
    disposition: 'capability',
    target: null,
  },

  // -------------------------------------------------------------------------
  // Reviewed canonical drill-ins added after the original Block A inventory.
  // These remain addressable because they preserve entity or workflow context;
  // none is a compatibility alias or a top-level product destination.
  // -------------------------------------------------------------------------
  {
    originalPath: '/first-time',
    destination: 'workbench',
    disposition: 'keep',
    target: null,
    note: 'public first-use authentication flow',
  },
  {
    originalPath: '/admin/operations',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'operational configuration owner',
  },
  {
    originalPath: '/changes/pending',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'approval queue',
  },
  {
    originalPath: '/design/runway-shapes',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'role-gated design-system reference',
  },
  {
    originalPath: '/identity-lifecycle/:type/workflow/:workflowId',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'workflow instance detail',
  },
  {
    originalPath: '/procurement/procurement',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'procurement workflow chair',
  },
  {
    originalPath: '/procurement/procurement/:id',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'procurement workflow instance',
  },
  {
    originalPath: '/narratives',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'narrative authoring catalog',
  },
  {
    originalPath: '/narratives/new',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'narrative authoring workflow',
  },
  {
    originalPath: '/narratives/new/:kind',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'typed narrative authoring workflow',
  },
  {
    originalPath: '/narratives/:id/compose',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'narrative editor',
  },
  {
    originalPath: '/qbr/:id/compose',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'QBR narrative editor',
  },
  {
    originalPath: '/qbr/new',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'QBR delivery workflow',
  },
  {
    originalPath: '/roadmaps/:id/compose',
    destination: 'library',
    disposition: 'capability',
    target: null,
    note: 'roadmap narrative editor',
  },
  {
    originalPath: '/patches/rollouts',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'fleet rollout list',
  },
  {
    originalPath: '/patches/rollouts/:id',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'fleet rollout detail',
  },
  {
    originalPath: '/proposals/new',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'proposal workflow',
  },
  {
    originalPath: '/proposals/:proposalId/wizard',
    destination: 'runway',
    disposition: 'capability',
    target: null,
    note: 'proposal workflow detail',
  },
  {
    originalPath: '/security/incidents/report',
    destination: 'portfolio',
    disposition: 'capability',
    target: null,
    note: 'incident narrative report',
  },
  {
    originalPath: '/security/incidents/:id',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'incident response detail',
  },
  {
    originalPath: '/tenant-setup/import',
    destination: 'switchboard',
    disposition: 'capability',
    target: null,
    note: 'tenant import workflow',
  },
  {
    originalPath: '/vault/credentials/:credentialId',
    destination: 'workbench',
    disposition: 'capability',
    target: null,
    note: 'credential detail',
  },

  // Dynamic record links that were emitted by dashboards but never had an
  // owning standalone route. Preserve the record context while landing in
  // the destination-owned workflow that can act on it.

  // -------------------------------------------------------------------------
  // Block C — design-system demo pages (admin / owner / engineer_l3 only).
  // -------------------------------------------------------------------------
  {
    originalPath: '/design',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives index',
  },
  {
    originalPath: '/design/stepper',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/inspector-panel',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/undo-redo',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/version-ribbon',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/quick-config-popover',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/dry-run-mode',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/status-pill',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/source-pill',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/dnd',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
  {
    originalPath: '/design/canvas-shell',
    destination: 'switchboard',
    disposition: 'keep',
    target: null,
    note: 'Block C primitives demo',
  },
] as const;

/**
 * Build a destination-hint lookup by original path. Used by AppShell to
 * recover legacy capability ownership and compatibility intent.
 */
export const DISPOSITION_BY_PATH: Record<string, RouteDisposition> = ROUTE_DISPOSITIONS.reduce(
  (acc, d) => {
    acc[d.originalPath] = d;
    return acc;
  },
  {} as Record<string, RouteDisposition>
);

/** Convenience accessor for the crawler. */
export function getDestinationForPath(path: string): DestinationId | null {
  return DISPOSITION_BY_PATH[path]?.destination ?? null;
}

/** Count routes per disposition — used by tests and the crawler report. */
export function dispositionCounts(): Record<Disposition, number> {
  return ROUTE_DISPOSITIONS.reduce(
    (acc, d) => {
      acc[d.disposition] = (acc[d.disposition] ?? 0) + 1;
      return acc;
    },
    { keep: 0, capability: 0, retired: 0 } as Record<Disposition, number>
  );
}
